import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Camera, Play, AlertCircle, RotateCcw } from 'lucide-react'
import { useApp } from '../../context/AppContext'
import { api } from '../../services/api'
import Webcam from 'react-webcam'
import { FaceLandmarker, FilesetResolver, DrawingUtils } from '@mediapipe/tasks-vision'

// Mobile detection utility function
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768
}

function FaceScanMediaPipe({ onScanComplete }) {
  const { user, isAuthenticated, isLoading, error: contextError, dispatch } = useApp()
  const [isScanning, setIsScanning] = useState(false)
  const [scanProgress, setScanProgress] = useState(0)
  const [scanId, setScanId] = useState(null)
  const [error, setError] = useState(null)
  const [capturedImage, setCapturedImage] = useState(null)
  const [fps, setFps] = useState(0)
  const [faceDetected, setFaceDetected] = useState(false)
  const [faceQuality, setFaceQuality] = useState('poor')
  const [faceLandmarker, setFaceLandmarker] = useState(null)
  const [faceLandmarkerReady, setFaceLandmarkerReady] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [scanResults, setScanResults] = useState(null)
  const [processingProgress, setProcessingProgress] = useState(0)
  const [processingStatus, setProcessingStatus] = useState('')
  const [capturedLandmarks, setCapturedLandmarks] = useState([])
  const [capturedQualityMetrics, setCapturedQualityMetrics] = useState([])

  const webcamRef = useRef(null)
  const canvasRef = useRef(null)
  const animationFrameRef = useRef(null)
  const drawingUtilsRef = useRef(null)
  const skipFramesRef = useRef(0)

  // Initialize MediaPipe Face Landmarker
  const initializeFaceLandmarker = useCallback(async () => {
    try {
      console.log('Initializing MediaPipe Face Landmarker...')

      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      )

      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          delegate: "GPU"
        },
        runningMode: "VIDEO",
        numFaces: 1,
        minFaceDetectionConfidence: 0.5,
        minFacePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false
      })

      setFaceLandmarker(landmarker)
      setFaceLandmarkerReady(true)

      // Initialize drawing utils
      drawingUtilsRef.current = new DrawingUtils(canvasRef.current?.getContext('2d'))

      console.log('Face Landmarker initialized successfully')
    } catch (error) {
      console.error('Failed to initialize Face Landmarker:', error)
      setError('Failed to initialize face detection')
    }
  }, [])

  // Initialize MediaPipe on component mount
  useEffect(() => {
    initializeFaceLandmarker()
  }, [initializeFaceLandmarker])

  // Assess face quality based on landmarks
  const assessFaceQuality = useCallback((landmarks) => {
    if (!landmarks || landmarks.length === 0) return 'poor'

    // Calculate face size (distance between temples)
    const leftTemple = landmarks[234] // Left temple point
    const rightTemple = landmarks[454] // Right temple point

    if (!leftTemple || !rightTemple) return 'poor'

    const canvas = canvasRef.current
    if (!canvas) return 'poor'

    const faceWidth = Math.abs(rightTemple.x - leftTemple.x) * canvas.width
    const faceHeight = Math.abs(landmarks[10].y - landmarks[152].y) * canvas.height // Top to bottom

    // Quality based on face size and position
    const minFaceSize = Math.min(canvas.width, canvas.height) * 0.3
    const maxFaceSize = Math.min(canvas.width, canvas.height) * 0.8

    if (faceWidth < minFaceSize * 0.7 || faceHeight < minFaceSize * 0.7) return 'poor'
    if (faceWidth < minFaceSize || faceHeight < minFaceSize) return 'fair'
    if (faceWidth > maxFaceSize || faceHeight > maxFaceSize) return 'fair'
    if (faceWidth > minFaceSize * 1.5 && faceHeight > minFaceSize * 1.5) return 'excellent'

    return 'good'
  }, [])

  // Calculate face size
  const calculateFaceSize = useCallback((landmarks, canvasWidth, canvasHeight) => {
    if (!landmarks || landmarks.length === 0) return 0

    const xs = landmarks.map(lm => lm.x * canvasWidth)
    const ys = landmarks.map(lm => lm.y * canvasHeight)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)

    const faceWidth = maxX - minX
    const faceHeight = maxY - minY
    return Math.sqrt(faceWidth * faceHeight)
  }, [])

  // Calculate brightness
  const calculateBrightness = useCallback((video) => {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      let brightness = 0

      for (let i = 0; i < data.length; i += 4) {
        brightness += (data[i] + data[i + 1] + data[i + 2]) / 3
      }

      return brightness / (data.length / 4)
    } catch (error) {
      return 128 // Default brightness
    }
  }, [])

  // Calculate contrast
  const calculateContrast = useCallback((video) => {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      let sum = 0
      let sumSquared = 0

      for (let i = 0; i < data.length; i += 4) {
        const gray = (data[i] + data[i + 1] + data[i + 2]) / 3
        sum += gray
        sumSquared += gray * gray
      }

      const mean = sum / (data.length / 4)
      const variance = (sumSquared / (data.length / 4)) - (mean * mean)
      return Math.sqrt(variance)
    } catch (error) {
      return 50 // Default contrast
    }
  }, [])

  // Calculate sharpness
  const calculateSharpness = useCallback((video) => {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      let sharpness = 0

      // Calculate Laplacian variance for sharpness
      for (let y = 1; y < canvas.height - 1; y++) {
        for (let x = 1; x < canvas.width - 1; x++) {
          const idx = (y * canvas.width + x) * 4
          const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3

          const left = data[((y * canvas.width + x - 1) * 4)] + data[((y * canvas.width + x - 1) * 4) + 1] + data[((y * canvas.width + x - 1) * 4) + 2]
          const right = data[((y * canvas.width + x + 1) * 4)] + data[((y * canvas.width + x + 1) * 4) + 1] + data[((y * canvas.width + x + 1) * 4) + 2]
          const top = data[(((y - 1) * canvas.width + x) * 4)] + data[(((y - 1) * canvas.width + x) * 4) + 1] + data[(((y - 1) * canvas.width + x) * 4) + 2]
          const bottom = data[(((y + 1) * canvas.width + x) * 4)] + data[(((y + 1) * canvas.width + x) * 4) + 1] + data[(((y + 1) * canvas.width + x) * 4) + 2]

          const laplacian = Math.abs(4 * gray - left - right - top - bottom)
          sharpness += laplacian
        }
      }

      return sharpness / ((canvas.width - 2) * (canvas.height - 2))
    } catch (error) {
      return 100 // Default sharpness
    }
  }, [])

  // Draw face mesh connections
  const drawFaceMesh = useCallback((ctx, landmarks, width, height) => {
    if (!landmarks || landmarks.length < 468) return

    ctx.strokeStyle = '#00FF00'
    ctx.lineWidth = isMobile() ? 3 : 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.globalAlpha = 0.8

    // Face contour (oval outline) - points 0-16
    ctx.beginPath()
    for (let i = 0; i < 17; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Left eyebrow - points 17-21
    ctx.beginPath()
    for (let i = 17; i < 22; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 17) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Right eyebrow - points 22-26
    ctx.beginPath()
    for (let i = 22; i < 27; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 22) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Nose bridge - points 27-30
    ctx.beginPath()
    for (let i = 27; i < 31; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 27) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Nose tip - points 31-35
    ctx.beginPath()
    for (let i = 31; i < 36; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 31) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Left eye (closed shape) - points 36-41
    ctx.beginPath()
    for (let i = 36; i < 42; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 36) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()
    ctx.stroke()

    // Right eye (closed shape) - points 42-47
    ctx.beginPath()
    for (let i = 42; i < 48; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 42) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()
    ctx.stroke()

    // Outer lips (closed shape) - points 48-59
    ctx.beginPath()
    for (let i = 48; i < 60; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 48) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()
    ctx.stroke()

    // Inner lips (closed shape) - points 60-67
    ctx.beginPath()
    for (let i = 60; i < 68; i++) {
      const point = landmarks[i]
      const x = point.x * width
      const y = point.y * height
      if (i === 60) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.closePath()
    ctx.stroke()
  }, [])

  // Real-time face detection and FPS tracking
  const startRealTimeDetection = useCallback(() => {
    if (!faceLandmarker || !webcamRef.current?.video) {
      console.log('Face landmarker or video not ready')
      return
    }

    const video = webcamRef.current.video
    const canvas = canvasRef.current

    // More robust video readiness check
    const checkVideoReady = () => {
      if (!video) return false

      // Check if video has loaded metadata and has dimensions
      const hasDimensions = video.videoWidth > 0 && video.videoHeight > 0
      const isReady = video.readyState >= 3 // HAVE_FUTURE_DATA or higher

      console.log('Video check:', {
        readyState: video.readyState,
        dimensions: `${video.videoWidth}x${video.videoHeight}`,
        hasDimensions,
        isReady
      })

      return hasDimensions && isReady
    }

    if (!checkVideoReady()) {
      console.log('Video not ready yet, retrying in 200ms...')
      setTimeout(() => startRealTimeDetection(), 200)
      return
    }

    console.log('Starting real-time detection with video:', video.videoWidth, 'x', video.videoHeight)

    // Setup canvas
    if (canvas) {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      console.log('Canvas setup:', canvas.width, 'x', canvas.height)

      // Ensure canvas display size matches video display size
      const videoElement = webcamRef.current?.video
      if (videoElement) {
        const videoRect = videoElement.getBoundingClientRect()
        canvas.style.width = videoRect.width + 'px'
        canvas.style.height = videoRect.height + 'px'
        console.log('Canvas display size set to:', videoRect.width, 'x', videoRect.height)
      }
    }

    let frameCount = 0
    let lastTime = performance.now()

    const processFrame = () => {
      const video = webcamRef.current?.video
      if (!video || video.readyState < 3) {
        console.log('Video not ready during processing, stopping detection')
        return
      }

      try {
        const results = faceLandmarker.detectForVideo(video, performance.now())

        // Clear canvas
        const ctx = canvas?.getContext('2d')
        if (canvas && ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
        }

        if (results.faceLandmarks && results.faceLandmarks.length > 0) {
          setFaceDetected(true)

          // Assess face quality based on landmarks
          const landmarks = results.faceLandmarks[0]
          const faceQualityScore = assessFaceQuality(landmarks)
          setFaceQuality(faceQualityScore)

          // Capture landmarks and quality metrics for analysis (throttled)
          if (landmarks && landmarks.length > 0 && skipFramesRef.current % 10 === 0) {
            const landmarkData = {
              timestamp: performance.now(),
              landmarks: landmarks.map(lm => ({
                x: lm.x * canvas.width,
                y: lm.y * canvas.height,
                z: lm.z || 0
              })),
              quality: faceQualityScore,
              confidence: landmarks.length || 0
            }

            setCapturedLandmarks(prev => [...prev, landmarkData])

            const qualityMetric = {
              timestamp: performance.now(),
              quality: faceQualityScore,
              faceSize: calculateFaceSize(landmarks, canvas.width, canvas.height),
              brightness: calculateBrightness(video),
              contrast: calculateContrast(video),
              sharpness: calculateSharpness(video)
            }

            setCapturedQualityMetrics(prev => [...prev, qualityMetric])
          }

          // Draw face mesh on canvas with connections
          if (canvas && ctx && drawingUtilsRef.current) {
            // Draw face mesh connections
            drawFaceMesh(ctx, landmarks, canvas.width, canvas.height)

            // Draw landmarks as dots
            drawingUtilsRef.current.drawLandmarks(landmarks, {
              radius: isMobile() ? 2 : 1.5,
              color: '#00FF00'
            })
          }

          // Increment skip frames counter
          skipFramesRef.current++
        } else {
          setFaceDetected(false)
          setFaceQuality('poor')
        }

        // Calculate FPS
        frameCount++
        const currentTime = performance.now()
        if (currentTime - lastTime >= 1000) {
          setFps(Math.round((frameCount * 1000) / (currentTime - lastTime)))
          frameCount = 0
          lastTime = currentTime
        }

        // Continue processing
        animationFrameRef.current = requestAnimationFrame(processFrame)
      } catch (error) {
        console.error('Error in real-time detection:', error)
        // Don't stop processing on error, just log it
      }
    }

    processFrame()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [faceLandmarker, assessFaceQuality])

  // Start real-time detection when camera and landmarker are ready
  useEffect(() => {
    if (cameraReady && faceLandmarkerReady && !isScanning) {
      // Add a small delay to ensure everything is properly initialized
      const timer = setTimeout(() => {
        console.log('Starting real-time detection...')
        const cleanup = startRealTimeDetection()
        return cleanup
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [cameraReady, faceLandmarkerReady, isScanning, startRealTimeDetection])

  // Handle camera ready state
  const handleCameraReady = useCallback(() => {
    console.log('Camera ready callback triggered')
    setCameraReady(true)
    setError(null)

    // Wait a bit more for video to be fully ready and update canvas
    setTimeout(() => {
      const video = webcamRef.current?.video
      if (video) {
        console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight)
        console.log('Video ready state:', video.readyState)

        // Update canvas size
        const canvas = canvasRef.current
        if (canvas && video.videoWidth > 0 && video.videoHeight > 0) {
          const videoRect = video.getBoundingClientRect()
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          canvas.style.width = videoRect.width + 'px'
          canvas.style.height = videoRect.height + 'px'
        }
      }
    }, 500)
  }, [])

  // Handle camera error
  const handleCameraError = useCallback((error) => {
    console.error('Camera error:', error)
    setError('Failed to access camera')
    setCameraReady(false)
  }, [])

  // Start face scan
  const startScan = async () => {
    if (!webcamRef.current || !user) return

    setIsScanning(true)
    setScanProgress(0)
    setFaceDetected(false)

    const video = webcamRef.current.video
    if (!video) return

    // Create stream - use canvas fallback for iOS Safari
    let stream
    let canvasElement = null
    let canvasContext = null
    let animationId = null

    // Try native captureStream first (works on desktop browsers)
    if (video.captureStream) {
      console.log('Using native video.captureStream()')
      stream = video.captureStream()
    } else {
      // Fallback for iOS Safari: use canvas-based capture
      console.log('Using canvas-based capture for iOS Safari')

      // Wait for video to be ready
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.error('Video dimensions not ready')
        setError('Camera not ready. Please try again.')
        setIsScanning(false)
        return
      }

      // Create hidden canvas
      canvasElement = document.createElement('canvas')
      canvasElement.width = video.videoWidth
      canvasElement.height = video.videoHeight
      canvasContext = canvasElement.getContext('2d')

      if (!canvasContext) {
        console.error('Failed to get canvas context')
        setError('Failed to initialize video recording')
        setIsScanning(false)
        return
      }

      // Draw video frames to canvas continuously
      const drawFrame = () => {
        if (canvasContext && canvasElement) {
          canvasContext.drawImage(video, 0, 0, canvasElement.width, canvasElement.height)
          animationId = requestAnimationFrame(drawFrame)
        }
      }
      drawFrame()

      // Capture stream from canvas (supported on iOS)
      try {
        stream = canvasElement.captureStream(30) // 30 fps
        console.log('Canvas stream created successfully')
      } catch (e) {
        console.error('Failed to capture stream from canvas:', e)
        if (animationId) cancelAnimationFrame(animationId)
        setError('Failed to initialize video recording')
        setIsScanning(false)
        return
      }
    }

    if (!stream) {
      console.error('No stream available')
      setError('Failed to initialize video recording')
      setIsScanning(false)
      return
    }

    // Robust mime type selection for iOS/Safari
    let mimeType = 'video/webm'
    const types = [
      'video/mp4',
      'video/mp4;codecs=avc1',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type
        break
      }
    }

    console.log('Using mime type:', mimeType)

    let mediaRecorder
    try {
      mediaRecorder = new MediaRecorder(stream, { mimeType })
    } catch (e) {
      console.error('Failed to create MediaRecorder with preferred mimeType, trying default:', e)
      try {
        mediaRecorder = new MediaRecorder(stream)
      } catch (e2) {
        console.error('Failed to create MediaRecorder:', e2)
        if (animationId) cancelAnimationFrame(animationId)
        setError('Failed to initialize video recording')
        setIsScanning(false)
        return
      }
    }

    const chunks = []
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data)
      }
    }

    const vitalsData = []
    const SCAN_DURATION_MS = 10000
    const VITALS_SAMPLE_MS = 100
    const scanStartTs = performance.now()

    // start recording and request periodic dataavailable events
    mediaRecorder.start(1000)

    // sample vitals and update progress on a fixed interval
    const processInterval = setInterval(async () => {
      const elapsed = performance.now() - scanStartTs
      const progressPct = Math.min(100, (elapsed / SCAN_DURATION_MS) * 100)

      if (faceDetected) {
        const vitals = {
          heartRate: Math.round(70 + Math.random() * 20),
          respiratoryRate: Math.round(16 + Math.random() * 4),
          oxygenSaturation: Math.round(95 + Math.random() * 5),
          stressLevel: Math.round(Math.random() * 0.5 * 100) / 100
        }
        vitalsData.push(vitals)
      }

      setScanProgress(progressPct)

      if (elapsed >= SCAN_DURATION_MS) {
        clearInterval(processInterval)
        mediaRecorder.stop()
      }
    }, VITALS_SAMPLE_MS)

    // hard stop fallback at SCAN_DURATION_MS in case interval is delayed
    setTimeout(() => {
      try {
        if (mediaRecorder.state === 'recording') {
          clearInterval(processInterval)
          mediaRecorder.stop()
        }
      } catch (_) { }
    }, SCAN_DURATION_MS + 200)

    mediaRecorder.onstop = async () => {
      // Clean up canvas animation if it was used
      if (animationId) {
        cancelAnimationFrame(animationId)
        console.log('Canvas animation stopped')
      }

      const blobType = mediaRecorder.mimeType || 'video/webm'
      const videoBlob = new Blob(chunks, { type: blobType })

      // Capture a photo
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0)
        const photoBlob = await new Promise((resolve) => {
          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8)
        })

        // Process the scan
        await processScanResults({
          videoBlob,
          photoBlob,
          landmarks: capturedLandmarks,
          qualityMetrics: capturedQualityMetrics,
          extractedVitals: vitalsData.length > 0 ? vitalsData : null,
          metadata: {
            duration: 10,
            faceDetected: faceDetected,
            faceQuality: faceQuality,
            scanType: 'face',
            timestamp: new Date().toISOString(),
            roiCount: vitalsData.length,
            signalSamples: vitalsData.length,
            landmarkCount: capturedLandmarks.length,
            qualityMetricCount: capturedQualityMetrics.length
          }
        })
      }
    }
  }

  // Process scan results and call backend APIs
  const processScanResults = async (scanData) => {
    setIsScanning(false)
    setIsProcessing(true)

    try {
      if (!user) {
        throw new Error('User profile not found')
      }

      // Start scan
      console.log('Starting scan for user:', user.id)
      const scanResponse = await api.post('/scan/start', {
        user_id: user.id
      }, {
        timeout: 10000 // 10 second timeout for scan start
      })

      const newScanId = scanResponse.data.id
      setScanId(newScanId)
      console.log('Scan started with ID:', newScanId)

      // Upload image, video, landmarks, and quality metrics for analysis
      console.log('Sending landmarks:', scanData.landmarks.length, 'frames')
      console.log('Sending quality metrics:', scanData.qualityMetrics.length, 'frames')

      const formData = new FormData()
      formData.append('file', scanData.photoBlob, 'face_scan.jpg')
      formData.append('video', scanData.videoBlob, 'face_scan.webm')
      formData.append('landmarks', JSON.stringify(scanData.landmarks))
      formData.append('quality_metrics', JSON.stringify(scanData.qualityMetrics))
      formData.append('metadata', JSON.stringify(scanData.metadata))

      const qualityResponse = await api.post('/scan/quality-check', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      // Analysis starts automatically after scan creation
      // Poll for completion
      let attempts = 0
      const maxAttempts = 60 // 5 minutes max

      const pollInterval = setInterval(async () => {
        try {
          const progressResponse = await api.get(`/scan/progress/${newScanId}`)
          const progress = progressResponse.data.progress_percentage || 0
          const status = progressResponse.data.status

          console.log('Scan progress:', { progress, status, scanId: newScanId })

          // Update processing UI
          setProcessingProgress(progress)
          setProcessingStatus(status)

          if (progress >= 100 && status === 'completed') {
            clearInterval(pollInterval)

            // Stop video capture and detection
            setIsScanning(false)
            setFaceDetected(false)
            if (webcamRef.current) {
              webcamRef.current.video.pause()
            }

            // Get final results
            const resultsResponse = await api.get(`/scan/result/${newScanId}`)
            const results = resultsResponse.data

            setScanResults(results)

            // Update overlay to show completion and navigate immediately
            setProcessingStatus('Generating health report...')

            // Navigate to report immediately, keep overlay visible during navigation
            setTimeout(() => {
              console.log('About to call onScanComplete with scanId:', newScanId)
              if (onScanComplete) {
                onScanComplete(newScanId)
              }
            }, 1000) // 1 second delay for completion message
          } else if (status === 'failed') {
            clearInterval(pollInterval)

            // Stop video capture and detection
            setIsScanning(false)
            setFaceDetected(false)
            if (webcamRef.current) {
              webcamRef.current.video.pause()
            }

            // Show error message
            setError('Scan failed. Please try again.')
            setIsProcessing(false)
            setProcessingProgress(0)
            setProcessingStatus('')
          }

          attempts++
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval)
            throw new Error('Scan processing timeout')
          }
        } catch (error) {
          console.error('Error polling scan progress:', error)
          clearInterval(pollInterval)
          setError('Failed to process scan results')
        }
      }, 5000)

    } catch (error) {
      console.error('Error processing scan results:', error)

      // Handle specific error types
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setError('Scan processing is taking longer than expected. Please wait or try again.')
      } else if (error.response?.status === 500) {
        setError('Server error during scan processing. Please try again.')
      } else if (error.response?.status === 403) {
        setError('Access denied. Please check your login status.')
      } else {
        setError('Failed to process scan results. Please try again.')
      }

      setIsProcessing(false)
    }
  }

  // Reset scan state
  const resetScan = () => {
    setIsScanning(false)
    setIsProcessing(false)
    setScanProgress(0)
    setScanId(null)
    setError(null)
    setCapturedImage(null)
    setFaceDetected(false)
    setFaceQuality('poor')
    setFps(0)
    setCameraReady(false)
    setFaceLandmarkerReady(false)
    setScanResults(null)
    setProcessingProgress(0)
    setProcessingStatus('')
    setCapturedLandmarks([])
    setCapturedQualityMetrics([])

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col overflow-hidden`}>
      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .mobile-scan-container {
            height: 100vh;
            height: 100dvh; /* Dynamic viewport height for mobile */
            overflow: hidden;
          }
          
          .mobile-camera-container {
            aspect-ratio: 3/4;
            max-height: 60vh;
            min-height: 300px;
          }
          
          .mobile-button {
            min-height: 44px; /* iOS touch target minimum */
            font-size: 16px; /* Prevent zoom on iOS */
          }
          
          .mobile-text {
            font-size: 14px;
            line-height: 1.4;
          }
          
          .mobile-small-text {
            font-size: 12px;
            line-height: 1.3;
          }
        }
        
        /* Prevent horizontal overflow */
        * {
          box-sizing: border-box;
        }
        
        /* Ensure canvas doesn't cause overflow */
        canvas {
          max-width: 100% !important;
          max-height: 100% !important;
        }
        
        /* Touch-friendly interactions */
        @media (max-width: 768px) {
          button, input, select, textarea {
            min-height: 44px;
            font-size: 16px;
          }
        }
        
        /* Safe area handling for mobile devices */
        @supports (padding: max(0px)) {
          .mobile-safe-area {
            padding-left: max(1rem, env(safe-area-inset-left));
            padding-right: max(1rem, env(safe-area-inset-right));
            padding-bottom: max(1rem, env(safe-area-inset-bottom));
          }
        }
      `}</style>

      {/* Main Content - Mobile Optimized Layout */}
      <main className={`flex-1 flex flex-col mobile-safe-area ${isMobile() ? 'mobile-scan-container' : 'px-2 py-2'} overflow-hidden`}>
        {isProcessing ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 mobile-text">Please Wait</h2>
              <p className="text-gray-600 mb-4 mobile-small-text">We are generating your health report...</p>
              <div className="w-full">
                <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2">
                  <span className="mobile-small-text">{processingStatus}</span>
                  <span className="mobile-small-text">{Math.round(processingProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            <div className={`flex-1 ${isMobile() ? 'flex flex-col space-y-3' : 'grid grid-cols-1 lg:grid-cols-2'} gap-2 min-h-0 max-w-full`}>
              {/* Camera Feed Section */}
              <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${isMobile() ? 'p-3' : 'p-2'} flex flex-col min-h-0 w-full`}>
                <h3 className={`font-semibold text-gray-900 ${isMobile() ? 'text-base mb-3' : 'text-sm my-2'} flex-shrink-0`}>
                  Camera Feed
                </h3>

                {/* Video Container - Mobile Optimized */}
                <div className="relative w-full">
                  <div className={`relative bg-black rounded-lg overflow-hidden shadow-sm mobile-camera-container w-full max-w-full`}>
                    <Webcam
                      ref={webcamRef}
                      className="w-full h-full object-cover bg-black"
                      playsInline={true}
                      videoConstraints={{
                        width: isMobile() ? { ideal: 480, max: 640 } : { ideal: 640, max: 800 },
                        height: isMobile() ? { ideal: 640, max: 800 } : { ideal: 480, max: 600 },
                        facingMode: 'user',
                        aspectRatio: isMobile() ? { ideal: 3 / 4 } : { ideal: 4 / 3 }
                      }}
                      onUserMedia={handleCameraReady}
                      onUserMediaError={handleCameraError}
                      style={{
                        minHeight: isMobile() ? '300px' : '200px',
                        maxHeight: isMobile() ? '60vh' : '60vh',
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%'
                      }}
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute top-0 left-0 pointer-events-none z-10"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}
                    />

                    {/* Debug Info with FPS - Mobile Optimized */}
                    <div className={`absolute top-2 left-2 bg-black bg-opacity-70 text-white ${isMobile() ? 'px-1.5 py-1' : 'px-2 py-1'} rounded text-xs font-mono z-20`}>
                      <div className={`flex ${isMobile() ? 'flex-row space-x-2' : 'flex-col space-y-1'}`}>
                        <span className={`flex items-center ${isMobile() ? 'space-x-1' : 'space-x-1'}`}>
                          <span className={isMobile() ? 'text-xs' : 'text-xs'}>Cam:</span>
                          <span className={cameraReady ? 'text-green-400' : 'text-red-400'}>
                            {cameraReady ? '✅' : '❌'}
                          </span>
                        </span>
                        <span className={`flex items-center ${isMobile() ? 'space-x-1' : 'space-x-1'}`}>
                          <span className={isMobile() ? 'text-xs' : 'text-xs'}>Face:</span>
                          <span className={faceDetected ? 'text-green-400' : 'text-red-400'}>
                            {faceDetected ? '✅' : '❌'}
                          </span>
                        </span>
                        {!isMobile() && (
                          <>
                            <span className="flex items-center space-x-1">
                              <span className="text-xs">MP:</span>
                              <span className={faceLandmarkerReady ? 'text-green-400' : 'text-red-400'}>
                                {faceLandmarkerReady ? '✅' : '❌'}
                              </span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <span className="text-xs">FPS:</span>
                              <span className="text-blue-400">{fps}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Face Status - Mobile Optimized */}
                <div className={`text-center ${isMobile() ? 'space-y-2 mt-3' : 'space-y-1 mt-2'} flex-shrink-0`}>
                  <div className="flex items-center justify-center space-x-2">
                    <div
                      className={`${isMobile() ? 'w-3 h-3' : 'w-2 h-2'} rounded-full animate-pulse`}
                      style={{
                        backgroundColor: faceDetected ?
                          (faceQuality === 'excellent' ? '#10B981' :
                            faceQuality === 'good' ? '#3B82F6' :
                              faceQuality === 'fair' ? '#F59E0B' : '#EF4444') : '#EF4444'
                      }}
                    ></div>
                    <span className={`${isMobile() ? 'text-sm' : 'text-xs'} font-semibold text-gray-800`}>
                      {faceDetected ? `Face Detected - ${faceQuality.charAt(0).toUpperCase() + faceQuality.slice(1)} Quality` : 'No Face Detected'}
                    </span>
                  </div>
                  <p className={`${isMobile() ? 'text-sm' : 'text-xs'} text-gray-600 px-1 mobile-small-text`}>
                    {faceDetected ?
                      (faceQuality === 'excellent' ? 'Perfect positioning! You can start the scan now.' :
                        faceQuality === 'good' ? 'Good positioning. You can start the scan now.' :
                          faceQuality === 'fair' ? 'Face detected but positioning could be better.' :
                            'Please improve your face positioning.') :
                      'Position your face in the camera frame for detection.'}
                  </p>
                </div>

                {error && (
                  <div className="mt-2 p-2 bg-red-50 rounded-lg flex-shrink-0">
                    <p className="text-xs text-red-800">{error}</p>
                  </div>
                )}

                {isScanning && (
                  <div className="mt-2 flex-shrink-0">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Scanning...</span>
                      <span>{Math.round(scanProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${scanProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className={`${isMobile() ? 'mt-3' : 'mt-2'} flex-shrink-0`}>
                  <button
                    onClick={startScan}
                    disabled={isScanning || isProcessing || !cameraReady || !faceLandmarkerReady || !faceDetected || faceQuality === 'poor'}
                    className={`w-full bg-primary-600 text-white ${isMobile() ? 'py-3 px-4' : 'py-2 px-3'} rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mobile-button ${isMobile() ? 'text-base' : 'text-sm'}`}
                  >
                    {isScanning ? 'Scanning...' :
                      isProcessing ? 'Processing...' :
                        !cameraReady || !faceLandmarkerReady ? 'Initializing...' :
                          !faceDetected ? 'Position Face in Camera' :
                            faceQuality === 'poor' ? 'Improve Face Position' :
                              'Start Face Scan'}
                  </button>
                </div>
              </div>

              {/* Right Side - Profile & Instructions */}
              <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${isMobile() ? 'p-3' : 'p-2'} flex flex-col ${isMobile() ? 'space-y-4' : 'space-y-3'} overflow-y-auto min-h-0 w-full`}>
                {/* Right Side Header */}
                <div className={`flex justify-between items-center flex-shrink-0 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg ${isMobile() ? 'p-4' : 'p-3'} border border-primary-200/50`}>
                  <h3 className={`${isMobile() ? 'text-lg' : 'text-xl'} font-bold text-primary-600`}>AI - Face Scan</h3>
                  <button
                    onClick={resetScan}
                    className={`${isMobile() ? 'px-3 py-2 text-sm' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm mobile-button`}
                  >
                    Reset
                  </button>
                </div>

                <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${isMobile() ? 'p-4' : 'p-3'}`}>
                  <h4 className={`${isMobile() ? 'text-base' : 'text-sm'} font-semibold text-gray-900 mb-2`}>Your Profile</h4>
                  <div className={`${isMobile() ? 'space-y-2' : 'space-y-1'} ${isMobile() ? 'text-sm' : 'text-xs'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium">{user.name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Age:</span>
                      <span className="font-medium">{user.age} years</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Gender:</span>
                      <span className="font-medium capitalize">{user.gender}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Height:</span>
                      <span className="font-medium">{user.height_cm} cm</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Weight:</span>
                      <span className="font-medium">{user.weight_kg} kg</span>
                    </div>
                  </div>
                </div>

                <div className={`bg-primary-50 rounded-lg ${isMobile() ? 'p-4' : 'p-3'}`}>
                  <h4 className={`${isMobile() ? 'text-sm' : 'text-xs'} font-medium text-primary-900 ${isMobile() ? 'mb-2' : 'mb-1'}`}>Scan Instructions:</h4>
                  <ul className={`${isMobile() ? 'text-sm' : 'text-xs'} text-primary-800 ${isMobile() ? 'space-y-1' : 'space-y-0.5'}`}>
                    <li>• Ensure good lighting on your face</li>
                    <li>• Position your face in the center of the frame</li>
                    <li>• Remove glasses or masks if possible</li>
                    <li>• Stay still during the 10-second scan</li>
                    <li>• Look directly at the camera</li>
                  </ul>
                </div>

                <div className={`bg-secondary-50 rounded-lg ${isMobile() ? 'p-4' : 'p-3'}`}>
                  <h4 className={`${isMobile() ? 'text-sm' : 'text-xs'} font-medium text-secondary-900 ${isMobile() ? 'mb-2' : 'mb-1'}`}>What We'll Analyze:</h4>
                  <ul className={`${isMobile() ? 'text-sm' : 'text-xs'} text-secondary-800 ${isMobile() ? 'space-y-1' : 'space-y-0.5'}`}>
                    <li>• Heart rate via rPPG technology</li>
                    <li>• Respiratory rate from micro-movements</li>
                    <li>• Oxygen saturation estimation</li>
                    <li>• Stress level from facial patterns</li>
                    <li>• BMI calculation from profile data</li>
                    <li>• Skin health analysis</li>
                    <li>• Eye health assessment</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default FaceScanMediaPipe
